// Fetch + cache for interactive-scene HTML artifacts (plan Part E, E3/E4).
//
// An artifact box no longer carries its document inline: `state._htmlFileId` is
// the uuid of an idealStudioFiles `interactive-scene-revision` row, and the
// document is read back from
//   GET {MW_API_URL}api/studio/interactive-html/:fileId
//     -> { success, html, fileId, navId }
// Revision rows are immutable (a new edit mints a new id), so a plain Map cache
// for the lifetime of the session is correct — no invalidation, no TTL. The
// endpoint itself answers `Cache-Control: private, immutable` for the same
// reason.
//
// Lives in common/ because BOTH bundles need it: the editor canvas, the Visor
// runtime render, and the SCORM/HTML export inliner. Auth is the same
// `Authorization: Bearer ${getToken()}` every other direct middleware call in
// this repo uses; getToken() covers the visor too (it falls back to the
// `window._requestVerificationToken` the visor page publishes from `?token=`).
import { useEffect, useState } from "react";
import { getToken } from "./environment_config.es6";
import { credentialsFor } from "./utils.es6";

const cache = new Map();
const inFlight = new Map();

// Synchronous peek, so a re-render of an already-fetched artifact renders the
// iframe immediately instead of flashing the loading placeholder.
export function peekArtifactHtml(fileId) {
  return (fileId && cache.get(fileId)) || null;
}

// Seed the cache with html the caller already holds (a chat-edit result carries
// both the html and the new revision id), so applying a revision doesn't
// immediately re-download what it just produced.
export function cacheArtifactHtml(fileId, html) {
  if (fileId && html) cache.set(fileId, html);
}

export function getArtifactHtml(fileId) {
  if (!fileId) return Promise.reject(new Error("missing-file-id"));
  if (cache.has(fileId)) return Promise.resolve(cache.get(fileId));
  if (inFlight.has(fileId)) return inFlight.get(fileId);

  const request = fetch(`${process.env.MW_API_URL}api/studio/interactive-html/${fileId}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((data) => {
      const html = (data && data.html) || "";
      if (!html) throw new Error("empty-artifact");
      cache.set(fileId, html);
      return html;
    })
    .finally(() => inFlight.delete(fileId));

  inFlight.set(fileId, request);
  return request;
}

// ponytail: gateway host is derived, not configured (no new env var). Convention
// (mw azure/asset-gateway.sh): mw.X -> assets.X, mwstage.X -> assets-stage.X,
// mwdev.X -> assets-dev.X. If hostnames ever stop following it, the Bearer is not
// sent, the gate answers 401 and motion fails closed into its error box.
export function gatewayOrigin(apiUrl) {
  const u = new URL(apiUrl);
  const [label, ...rest] = u.hostname.split(".");
  const m = /^mw(.*)$/.exec(label);
  if (!m) return null;
  return `${u.protocol}//${["assets" + (m[1] ? "-" + m[1] : ""), ...rest].join(".")}${u.port ? ":" + u.port : ""}`;
}

// Bearer goes ONLY to {API origin (any protocol) | https derived gateway origin}/api/f/**.
// ponytail: plain-http API origin (local dev) is no new trust - getArtifactHtml
// already sends the Bearer to that exact origin.
export function mayCarryBearer(u) {
  if (!u.pathname.startsWith("/api/f/")) return false;
  const base =
    (typeof window !== "undefined" && window.ideaLibraryRootUrl) || process.env.MW_API_URL;
  if (!base) return false;
  try {
    const api = new URL(base, window.location.href);
    return (
      u.origin === api.origin || (u.protocol === "https:" && u.origin === gatewayOrigin(api.href))
    );
  } catch (e) {
    return false;
  }
}

// "mw" = fetch it (Bearer only if mayCarryBearer, else cookies at most), "direct" =
// any other http(s) url (iframe src, never fetched, never sent the token),
// "invalid" = unparseable or non-http(s). htmlUrl is editable course content, so
// the Bearer token must never follow it to a foreign host.
export function classifyHtmlUrl(url) {
  try {
    const u = new URL(url, window.location.href);
    if (u.protocol !== "http:" && u.protocol !== "https:") return "invalid";
    if (!u.pathname.startsWith("/api/f/")) return "direct";
    if (mayCarryBearer(u)) return "mw";
    return credentialsFor(url).credentials === "include" ? "mw" : "direct";
  } catch (e) {
    return "invalid";
  }
}

// Motion/3D scene documents are addressed by URL (state.htmlUrl), typically the
// mw asset gate, which serves HTML as an attachment with frame-ancestors 'none'
// — so an iframe cannot navigate to it. Fetch it with the same Bearer token and
// render it as srcdoc instead. Cached per URL (the gate's files are immutable).
export function getHtmlByUrl(url) {
  if (!url) return Promise.reject(new Error("missing-url"));
  if (classifyHtmlUrl(url) !== "mw") return Promise.reject(new Error("not-mw-url"));
  if (cache.has(url)) return Promise.resolve(cache.get(url));
  if (inFlight.has(url)) return inFlight.get(url);
  const request = fetch(url, {
    headers: mayCarryBearer(new URL(url, window.location.href))
      ? { Authorization: `Bearer ${getToken()}` }
      : {},
    ...credentialsFor(url),
  })
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((html) => {
      if (!html) throw new Error("empty-document");
      cache.set(url, html);
      return html;
    })
    .finally(() => inFlight.delete(url));
  inFlight.set(url, request);
  return request;
}

// { html, failed, directSrc } for an iframe: `srcDoc={html}` for mw urls, else
// `src={directSrc}` for foreign http(s) urls. html is null until fetched.
export function useHtmlByUrl(url) {
  const kind = url ? classifyHtmlUrl(url) : null;
  const [html, setHtml] = useState(() => (kind === "mw" ? peekArtifactHtml(url) : null));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setHtml(kind === "mw" ? peekArtifactHtml(url) : null);
    setFailed(false);
    if (kind !== "mw") return undefined;
    let cancelled = false;
    getHtmlByUrl(url)
      .then((h) => !cancelled && setHtml(h))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [url]);
  return { html, failed: failed || kind === "invalid", directSrc: kind === "direct" ? url : null };
}

// Every distinct artifact reference held by a course state, deduped.
export function collectHtmlFileIds(state) {
  const toolbars = (state && state.pluginToolbarsById) || {};
  const ids = new Set();
  Object.keys(toolbars).forEach((boxId) => {
    const fileId = toolbars[boxId] && toolbars[boxId].state && toolbars[boxId].state._htmlFileId;
    if (fileId) ids.add(fileId);
  });
  return [...ids];
}

// Pure: return a COPY of `state` with every `_htmlFileId` replaced by the inline
// `_html` it points at. Kept separate from the fetching so the export path's
// swap is testable on a plain fixture.
export function swapHtmlArtifactRefs(state, htmlById) {
  const toolbars = state && state.pluginToolbarsById;
  if (!toolbars) return state;
  let changed = false;
  const next = {};
  const swappedById = {};
  Object.keys(toolbars).forEach((boxId) => {
    const toolbar = toolbars[boxId];
    const fileId = toolbar && toolbar.state && toolbar.state._htmlFileId;
    const html = fileId ? htmlById[fileId] : null;
    if (!html) {
      next[boxId] = toolbar;
      return;
    }
    changed = true;
    const boxState = { ...toolbar.state, _html: html };
    delete boxState._htmlFileId;
    next[boxId] = { ...toolbar, state: boxState };
    swappedById[boxId] = html;
  });
  if (!changed) return state;
  return {
    ...state,
    pluginToolbarsById: next,
    boxesById: swapBoxMirrors(state.boxesById, swappedById),
  };
}

// Course-gen (interactive-scene-builder.service.ts) and the E5 migration mirror
// the reference onto `boxesById[boxId].state` as well as the toolbar. Nothing
// READS that mirror — `pluginToolbarsById` is authoritative and UPDATE_BOX
// (reducers/boxes_by_id.es6:378) never rewrites `box.state` — so after a
// chat-edit it still holds the PREVIOUS revision id. Left alone it ships in the
// offline package as a dangling `_htmlFileId`, so it is swapped here too, using
// the TOOLBAR's html rather than its own stale id.
function swapBoxMirrors(boxes, htmlByBoxId) {
  if (!boxes) return boxes;
  const ids = Object.keys(htmlByBoxId).filter(
    (boxId) => boxes[boxId] && boxes[boxId].state && boxes[boxId].state._htmlFileId
  );
  if (ids.length === 0) return boxes;
  const nextBoxes = { ...boxes };
  ids.forEach((boxId) => {
    const boxState = { ...nextBoxes[boxId].state, _html: htmlByBoxId[boxId] };
    delete boxState._htmlFileId;
    nextBoxes[boxId] = { ...nextBoxes[boxId], state: boxState };
  });
  return nextBoxes;
}

// LOAD-BEARING for offline packages (SCORM / HTML zip): the exported bundle has
// no token and no middleware to call, so an artifact left as a `_htmlFileId`
// reference renders as a permanently-loading box. Every ref must be resolved
// into the SERIALIZED copy before the package is written — and if any fetch
// fails the export must abort rather than ship a broken scene, which is why
// this rejects instead of falling back.
export async function inlineHtmlArtifacts(state) {
  const ids = collectHtmlFileIds(state);
  if (ids.length === 0) return state;
  const htmls = await Promise.all(ids.map((id) => getArtifactHtml(id)));
  const htmlById = {};
  ids.forEach((id, i) => {
    htmlById[id] = htmls[i];
  });
  return swapHtmlArtifactRefs(state, htmlById);
}
